import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  ValamarAppInitModel,
  ValamarAppInitVariables,
  ValamarAppModel,
  ValamarAppVariables,
} from '../../../../models/email-template/valamar-app.interface';

/**
 * Mockup-styled Valamar App block - a drop-in replacement for
 * app-valamar-app in block-renderer-v2 (same [block]/[isPreview] inputs and
 * outputs), so it covers both the center-canvas summary
 * (isPreview=true) and the right-panel edit form (isPreview=false). Reuses
 * the real, unmodified app-block-text (image upload/segment/validation) and
 * app-field-editor-v2/app-pin sub-components and the exact same
 * ValamarAppVariables field names - only the surrounding layout is new. The
 * two store buttons are fixed "Google Play"/"App Store" labels - matches the
 * mockup (which renders fixed store-badge images) and v1 (whose own labels
 * are hardcoded strings, not stored fields - there's no AppCta1Text/
 * AppCta2Text in ValamarAppVariables, only the two links are editable).
 */
@Component({
  selector: 'app-valamar-app-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './valamar-app-v2.component.html',
  styleUrl: './valamar-app-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ValamarAppComponentV2 extends BaseComponent implements OnInit {
  @Input() block: ValamarAppModel = new ValamarAppInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new ValamarAppInitVariables();
    }
  }

  imagePlaceholder = 'Image';
  imageHrefPlaceholder = 'Enter Image Redirect Link';
  imageAltPlaceholder = 'Enter Image Alt';
  headingPlaceholder = 'Enter Heading Text';
  textPlaceholder = 'Enter Text';
  ctaLink1Placeholder = 'Enter First CTA Link';
  ctaLink2Placeholder = 'Enter Second CTA Link';

  protected imageAsUpload = true;

  protected toggleImageMode(): void {
    this.imageAsUpload = !this.imageAsUpload;
  }

  private hrefFor(link?: string, segment?: string): string {
    const base = this.helper.getFullUrl(link);
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  get imagePreviewHref(): string {
    return this.hrefFor(this.block.variables?.['AppImageHref'], this.block.variables?.['AppImageHrefSegmentRegex']);
  }

  get cta1Href(): string {
    return this.hrefFor(this.block.variables?.['AppCta1Link'], this.block.variables?.['AppCta1LinkSegmentRegex']);
  }

  get cta2Href(): string {
    return this.hrefFor(this.block.variables?.['AppCta2Link'], this.block.variables?.['AppCta2LinkSegmentRegex']);
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof ValamarAppVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
